import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

/** GeoJSON-like point used by map markers (`[lng, lat]`). */
export type ShopAroundListBodyPreviewPoint = {
  point: { coordinates: readonly [number, number] };
};

/** Serializable shop row used in CB2 preview scenarios. */
export type ShopAroundListBodyPreviewShop = {
  id: string;
  name: string;
  slug: string;
  available: boolean;
  short_description: string;
  pricing: string;
  distance: number;
  entity_type: "shop";
  eat_in: boolean;
  status: string;
  currency: string;
  type: { name: string; thumbnail_url: string };
  images?: {
    overview?: { url: string; width: number; height: number };
  };
  /** Optional venue point so map preview can place markers. */
  venue?: ShopAroundListBodyPreviewPoint;
  /** Optional address point (fallback for map marker resolution). */
  address?: ShopAroundListBodyPreviewPoint;
  schedules?: readonly {
    start: number;
    end: number;
    opened: boolean;
    active: boolean;
    /** Must be `null` (not omitted) so schedule filters accept all distribution modes. */
    allowed_distribution_modes: null;
  }[];
};

/**
 * Serializable preview VM for `ShopAroundListBodyBlockComponent`.
 * Kept in content-builder so dashboard + webapp share the same transport shape.
 */
export type ShopAroundListBodyBlockScenarioContent = {
  /** `undefined` while loading; an array (possibly empty) once loaded. */
  shops: readonly ShopAroundListBodyPreviewShop[] | undefined;
  wantedAt: string | null;
};

/** Legacy CB1 field order for `ShopAroundListBodyBlock`. */
export const SHOP_AROUND_LIST_BODY_LEGACY_FIELD_ORDER = [
  "template_layout",
  { type: "separator" },
  "heading",
  "heading_color",
  "entity_name_color",
  "entity_meta_color",
  "entity_address_color",
  { type: "separator" },
  "display_entity_images",
  "display_entity_categories",
  "display_entity_distances",
  "display_entity_pricings",
  "display_entity_addresses",
  "is_shop_name_visible",
  "entity_names_size",
  { type: "separator" },
  "display_see_shop_map_button",
  "shops_map_button_color",
  "shops_map_button_text_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  shopAroundListBodyBlockDescriptor: _generatedShopAroundListBodyBlockDescriptor,
  shopAroundListBodyBlockBlocDefinition,
  shopAroundListBodyBlockDashboardLayout,
  shopAroundListBodyBlockConfig,
  shopAroundListBodyBlockResolveConfig,
  shopAroundListBodyBlockProperties,
  shopAroundListBodyBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    id: "shop-around-list-block",
    name: "Shop around list body",
    componentType: "ShopAroundListBodyBlockComponent",
    category: "catalog",
    description: "Shop search result list.",
    keywords: ["shop", "search", "list"],
    allowedIn: [{ bloc: "ShopSearchPageComponent", slot: "body" }],
    sharedFields: {},
  },

  () => ({
    template_layout: propertyValue({
      type: "select",
      value: "default",
      options: {
        label: "pages.blocks.shop-around-list-body.template-layout.label",
        help: "pages.blocks.shop-around-list-body.template-layout.help",
        choices: [
          {
            value: "default",
            label: "pages.blocks.shop-around-list-body.template-layout.options.default",
          },
          {
            value: "hero",
            label: "pages.blocks.shop-around-list-body.template-layout.options.hero",
          },
        ],
      },
    }),
    heading: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.shop-around-list-body.heading.label",
        help: "pages.blocks.shop-around-list-body.heading.help",
      },
    }),
    heading_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "primary-800" },
      options: {
        label: "pages.blocks.shop-around-list-body.heading-color.label",
      },
    }),
    entity_name_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-900" },
      options: {
        label: "pages.blocks.shop-around-list-body.entity-name-color.label",
      },
    }),
    entity_meta_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label: "pages.blocks.shop-around-list-body.entity-meta-color.label",
        help: "pages.blocks.shop-around-list-body.entity-meta-color.help",
      },
    }),
    entity_address_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-700" },
      options: {
        label: "pages.blocks.shop-around-list-body.entity-address-color.label",
      },
    }),
    display_entity_images: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.shop-around-list-body.display-entity-images.label",
      },
    }),
    display_entity_categories: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.shop-around-list-body.display-entity-categories.label",
        help: "pages.blocks.shop-around-list-body.display-entity-categories.help",
      },
    }),
    display_entity_distances: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.shop-around-list-body.display-entity-distances.label",
      },
    }),
    display_entity_pricings: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.shop-around-list-body.display-entity-pricings.label",
      },
    }),
    display_entity_addresses: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label: "pages.blocks.shop-around-list-body.display-entity-addresses.label",
      },
    }),
    entity_names_size: propertyValue({
      type: "select",
      value: "lg",
      options: {
        label: "pages.blocks.shop-around-list-body.entity-names-size.label",
        choices: [
          {
            value: "lg",
            label: "pages.blocks.shop-around-list-body.entity-names-size.options.lg",
          },
          {
            value: "2xl",
            label: "pages.blocks.shop-around-list-body.entity-names-size.options.2xl",
          },
          {
            value: "3xl",
            label: "pages.blocks.shop-around-list-body.entity-names-size.options.3xl",
          },
        ],
      },
    }),
    display_see_shop_map_button: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.shop-around-list-body.display-see-shop-map-button.label",
        help: "pages.blocks.shop-around-list-body.display-see-shop-map-button.help",
      },
    }),
    shops_map_button_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "primary-100" },
      options: {
        label: "pages.blocks.shop-around-list-body.shops-map-button-color.label",
      },
    }),
    shops_map_button_text_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#000000" },
      options: {
        label: "pages.blocks.shop-around-list-body.shops-map-button-text-color.label",
      },
    }),
    card_template: propertyValue({
      type: "select",
      value: "big_image",
      options: {
        label: "pages.blocks.shop-around-list-body.card-template.label",
        choices: [
          {
            value: "big_image",
            label: "pages.blocks.shop-around-list-body.card-template.options.big_image",
          },
          {
            value: "small_image_with_button",
            label:
              "pages.blocks.shop-around-list-body.card-template.options.small_image_with_button",
          },
        ],
      },
    }),
    is_shop_name_visible: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.shop-around-list-body.is-shop-name-visible.label",
        legacyHandle: "IsShopNameVisible",
      },
    }),
  }),

  () => ({
    omitFields: ["card_template"] as const,
    tabs: [
      {
        label: "pages.blocks.shop-around-list-body.tab.content.label",
        fieldsets: [
          {
            legend:
              "pages.blocks.shop-around-list-body.fieldset.layout.label",
            fields: ["template_layout"],
          },
          {
            legend:
              "pages.blocks.shop-around-list-body.fieldset.heading.label",
            fields: ["heading", "heading_color"],
          },
          {
            legend:
              "pages.blocks.shop-around-list-body.fieldset.shop-card.label",
            fields: [
              "is_shop_name_visible",
              {
                prop: "entity_names_size",
                visibleWhen: { is_shop_name_visible: true },
              },
              {
                prop: "entity_name_color",
                visibleWhen: { is_shop_name_visible: true },
              },
              "display_entity_images",
              "display_entity_categories",
              "display_entity_distances",
              "display_entity_pricings",
              "entity_meta_color",
              "display_entity_addresses",
              {
                prop: "entity_address_color",
                visibleWhen: { display_entity_addresses: true },
              },
            ],
          },
          {
            legend:
              "pages.blocks.shop-around-list-body.fieldset.map-button.label",
            fields: [
              "display_see_shop_map_button",
              {
                prop: "shops_map_button_color",
                visibleWhen: { display_see_shop_map_button: true },
              },
              {
                prop: "shops_map_button_text_color",
                visibleWhen: { display_see_shop_map_button: true },
              },
            ],
          },
        ],
      },
    ],
  }),
);

export function shopAroundListBodyBlockDescriptor(): IBlockDescriptor;
export function shopAroundListBodyBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function shopAroundListBodyBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedShopAroundListBodyBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedShopAroundListBodyBlockDescriptor,
    shopAroundListBodyBlockProperties,
    fieldOrder,
  );
}

export {
  shopAroundListBodyBlockBlocDefinition,
  shopAroundListBodyBlockDashboardLayout,
  shopAroundListBodyBlockConfig,
  shopAroundListBodyBlockResolveConfig,
  shopAroundListBodyBlockProperties,
  shopAroundListBodyBlockDefinitionSetupScenarios,
};

export type ShopAroundListBodyBlockConfig = typeof shopAroundListBodyBlockConfig;
